import { useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import { SELECTOR_PAGE_SIZE } from '../../lib/constants'
import {
  fractionToPctInput,
  isOverCommissionCap,
  normalizeAdvisorList,
  pctInputToFraction,
} from '../../lib/advisors'
import {
  PipelineStageLabel,
  type AdvisorDto,
  type OpportunityDetail,
  type ClientListItem,
  type ClientListResponse,
  type PipelineStage,
} from '@toga/shared'
import { useServiceLines } from '../../lib/service-lines'
import ServiceLineEditor, {
  draftsToServiceLinesInput,
  newDraft,
  type ServiceLineDraft,
} from './ServiceLineEditor'

interface Props {
  onClose: () => void
  onCreated: (detail: OpportunityDetail) => void
  defaultClientId?: string
}

export default function NewCaseModal({ onClose, onCreated, defaultClientId }: Props) {
  const { t } = useTranslation(['opportunities', 'common', 'enums'])
  const navigate = useNavigate()
  const { lines: serviceLines, nameOf, failed: serviceLinesFailed } = useServiceLines()
  const [clients, setClients] = useState<ClientListItem[] | null>(null)
  const [clientId, setClientId] = useState(defaultClientId ?? '')
  const [title, setTitle] = useState('')
  const [stage, setStage] = useState<PipelineStage>('LEAD')
  const [description, setDescription] = useState('')
  const [advisors, setAdvisors] = useState<AdvisorDto[]>([])
  const [advisorId, setAdvisorId] = useState('')
  const [advisorCommissionPct, setAdvisorCommissionPct] = useState('')
  // LOTE B — líneas de servicio de la oportunidad (importe + facturación +
  // recurrencia por línea).
  const [drafts, setDrafts] = useState<ServiceLineDraft[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Al cargar el catálogo, deja marcada por defecto la línea isDefault (o la
  // primera activa). Solo UNA vez.
  const [defaultApplied, setDefaultApplied] = useState(false)
  useEffect(() => {
    if (defaultApplied || serviceLines.length === 0) return
    const def = serviceLines.find((l) => l.isDefault) ?? serviceLines[0]!
    setDrafts((prev) => (prev.length > 0 ? prev : [newDraft(def.code)]))
    setDefaultApplied(true)
  }, [serviceLines, defaultApplied])

  useEffect(() => {
    void (async () => {
      try {
        const resp = await api.get<ClientListResponse>(`/clients?pageSize=${SELECTOR_PAGE_SIZE}&sortBy=name&sortDir=asc`)
        setClients(resp.items)
        if (!defaultClientId && resp.items[0]) setClientId(resp.items[0].id)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('newCase.errors.loadClients'))
      }
    })()
  }, [defaultClientId])

  useEffect(() => {
    void (async () => {
      try {
        const resp = await api.get<unknown>('/advisors?active=true')
        setAdvisors(normalizeAdvisorList(resp))
      } catch {
        setAdvisors([])
      }
    })()
  }, [])

  const overCap = isOverCommissionCap(advisorCommissionPct)

  const onPickAdvisor = (id: string) => {
    setAdvisorId(id)
    const a = advisors.find((x) => x.id === id)
    setAdvisorCommissionPct(a ? fractionToPctInput(a.defaultCommissionPct) : '')
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (advisorId && overCap) {
      setError(t('newCase.errors.commissionCap'))
      return
    }
    if (drafts.length === 0) {
      setError(t('newCase.errors.noServiceLine'))
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const payload = {
        title,
        clientId,
        stage,
        description: description || null,
        advisorId: advisorId || null,
        advisorCommissionPct: advisorId ? pctInputToFraction(advisorCommissionPct) : null,
        // LOTE B — detalle por línea (fuente de verdad; el backend deriva el resto).
        serviceLines: draftsToServiceLinesInput(drafts),
      }
      const { opportunity: created } = await api.post<{ opportunity: OpportunityDetail }>(
        '/opportunities',
        payload
      )
      onCreated(created)
      navigate(`/opportunities/${created.id}`)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const fieldErr = body?.issues?.fieldErrors
        const firstFieldErr = fieldErr ? Object.values(fieldErr).flat().find(Boolean) : undefined
        setError(firstFieldErr ?? body?.error ?? t('newCase.errors.createFailed'))
      } else {
        setError(t('newCase.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal open onClose={onClose} title={t('newCase.title')} maxWidth="max-w-2xl">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('newCase.fields.client')}</label>
          {clients === null ? (
            <div className="t-muted text-sm">
              <Loader2 size={14} className="inline animate-spin mr-2" /> {t('newCase.loadingClients')}
            </div>
          ) : clients.length === 0 ? (
            <div className="text-sm text-amber-700 dark:text-amber-300">
              {t('newCase.noClients')}
            </div>
          ) : (
            <select
              required
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className={inputCls}
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                  {c.nationalId ? ` · ${c.nationalId}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('newCase.fields.title')}</label>
          <input
            required
            minLength={2}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('newCase.placeholders.title')}
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('newCase.fields.initialStage')}</label>
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value as PipelineStage)}
            className={inputCls}
          >
            {Object.entries(PipelineStageLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        {/* LOTE B — líneas de servicio con importe, facturación y recurrencia
            por línea. Marca al menos UNA. */}
        <fieldset className="rounded-lg border border-[var(--border)] bg-[var(--bg-input-soft)] p-3 space-y-2">
          <legend className="px-1 text-xs t-muted">{t('newCase.serviceLines.legend')}</legend>
          {serviceLinesFailed && serviceLines.length === 0 && (
            <p className="text-xs text-rose-700 dark:text-rose-300">
              {t('newCase.errors.loadServiceLines')}
            </p>
          )}
          <ServiceLineEditor
            drafts={drafts}
            onChange={setDrafts}
            catalogLines={serviceLines.map((l) => ({ code: l.code, name: l.name }))}
            nameOf={nameOf}
          />
        </fieldset>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('newCase.fields.advisor')}</label>
            <select
              value={advisorId}
              onChange={(e) => onPickAdvisor(e.target.value)}
              className={inputCls}
            >
              <option value="">{t('newCase.advisorNone')}</option>
              {advisors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          {advisorId && (
            <div>
              <label className="block text-xs t-muted mb-1.5">{t('newCase.fields.advisorCommission')}</label>
              <input
                value={advisorCommissionPct}
                onChange={(e) => setAdvisorCommissionPct(e.target.value)}
                placeholder="5"
                inputMode="decimal"
                className={`${inputCls} font-mono ${overCap ? '!border-rose-500/60' : ''}`}
              />
              {overCap && (
                <p className="text-[11px] text-rose-700 dark:text-rose-300 mt-1">
                  {t('newCase.maxCommission')}
                </p>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('newCase.fields.description')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={inputCls}
          />
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !clientId || !title || drafts.length === 0 || (Boolean(advisorId) && overCap)}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {t('newCase.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
